import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, CalendarClock, ClipboardCheck, Receipt, FileText } from "lucide-react";
import { adminListCalendarEvents } from "@/lib/portal/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/calendar")({
  component: CalendarPage,
});

type View = "day" | "week" | "month";
type Event = {
  id: string;
  type: "appointment" | "task" | "invoice" | "proposal";
  title: string;
  subtitle?: string;
  start: string;
  end?: string;
  href: string;
  status?: string;
};

const TYPE_STYLES: Record<Event["type"], { badge: string; dot: string; icon: any; label: string }> = {
  appointment: { badge: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30", dot: "bg-emerald-400", icon: CalendarClock, label: "Appt" },
  task:        { badge: "bg-amber-300/15 text-amber-200 border-amber-300/30", dot: "bg-amber-300", icon: ClipboardCheck, label: "Task" },
  invoice:     { badge: "bg-rose-400/15 text-rose-200 border-rose-400/30", dot: "bg-rose-400", icon: Receipt, label: "Invoice" },
  proposal:    { badge: "bg-sky-400/15 text-sky-200 border-sky-400/30", dot: "bg-sky-400", icon: FileText, label: "Proposal" },
};

function startOfDay(d: Date) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function endOfDay(d: Date) { const x = new Date(d); x.setHours(23,59,59,999); return x; }
function startOfWeek(d: Date) { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; }
function addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23,59,59,999); }

function computeRange(view: View, anchor: Date) {
  if (view === "day") return { from: startOfDay(anchor), to: endOfDay(anchor) };
  if (view === "week") { const s = startOfWeek(anchor); return { from: s, to: endOfDay(addDays(s, 6)) }; }
  const s = startOfMonth(anchor); const e = endOfMonth(anchor);
  // pad to full weeks so the grid stays complete
  const gridStart = startOfWeek(s);
  const gridEnd = endOfDay(addDays(startOfWeek(e), 6));
  return { from: gridStart, to: gridEnd };
}

function CalendarPage() {
  const [view, setView] = useState<View>("week");
  const [anchor, setAnchor] = useState<Date>(() => new Date());

  const range = useMemo(() => computeRange(view, anchor), [view, anchor]);
  const fn = useServerFn(adminListCalendarEvents);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "calendar", view, range.from.toISOString(), range.to.toISOString()],
    queryFn: () => fn({ data: { from: range.from.toISOString(), to: range.to.toISOString() } }),
  });
  const events: Event[] = (data?.events ?? []) as Event[];

  const eventsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const e of events) {
      const k = new Date(e.start).toDateString();
      const arr = map.get(k) ?? [];
      arr.push(e);
      map.set(k, arr);
    }
    return map;
  }, [events]);

  function shift(dir: -1 | 1) {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  }

  const label = useMemo(() => {
    if (view === "day") return anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    if (view === "week") { const s = startOfWeek(anchor); const e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined,{month:"short",day:"numeric"})} - ${e.toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}`; }
    return anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [view, anchor]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-amber-300 text-[11px] tracking-[0.3em] uppercase font-semibold">Calendar</p>
          <h1 className="font-serif text-4xl text-white mt-1">Schedule</h1>
          <p className="text-zinc-400 text-sm mt-1">Appointments, tasks, invoice due dates, and proposal milestones.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
            {(["day","week","month"] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest ${view===v?"bg-amber-300/20 text-amber-200":"text-zinc-400 hover:text-white"}`}
              >{v}</button>
            ))}
          </div>
          <button onClick={() => setAnchor(new Date())} className="px-3 py-1.5 text-xs uppercase tracking-widest rounded-lg border border-white/10 text-zinc-300 hover:text-white">Today</button>
          <div className="flex items-center rounded-lg border border-white/10">
            <button onClick={() => shift(-1)} className="p-1.5 text-zinc-300 hover:text-white" aria-label="Previous"><ChevronLeft className="size-4" /></button>
            <button onClick={() => shift(1)} className="p-1.5 text-zinc-300 hover:text-white" aria-label="Next"><ChevronRight className="size-4" /></button>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-between">
        <p className="text-white font-serif text-xl">{label}</p>
        {isFetching && <Loader2 className="size-4 animate-spin text-amber-300" />}
      </div>

      {isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
      ) : view === "day" ? (
        <DayView date={anchor} events={eventsByDay.get(anchor.toDateString()) ?? []} />
      ) : view === "week" ? (
        <WeekView anchor={anchor} eventsByDay={eventsByDay} />
      ) : (
        <MonthView anchor={anchor} eventsByDay={eventsByDay} onPickDay={(d) => { setAnchor(d); setView("day"); }} />
      )}

      <Legend />
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-zinc-400">
      {(Object.entries(TYPE_STYLES) as [Event["type"], typeof TYPE_STYLES[Event["type"]]][]).map(([k, s]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={`size-2 rounded-full ${s.dot}`} /> {s.label}
        </span>
      ))}
    </div>
  );
}

function EventPill({ e, compact }: { e: Event; compact?: boolean }) {
  const s = TYPE_STYLES[e.type];
  const t = new Date(e.start);
  const time = t.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return (
    <Link to={e.href} className={`block rounded-md border ${s.badge} px-2 py-1 hover:opacity-90`}>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`size-1.5 rounded-full ${s.dot} shrink-0`} />
        <span className="text-[11px] font-medium truncate">{time} · {e.title}</span>
      </div>
      {!compact && e.subtitle && <p className="text-[10px] text-zinc-300/80 truncate mt-0.5">{e.subtitle}</p>}
    </Link>
  );
}

function DayView({ date, events }: { date: Date; events: Event[] }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      {events.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center">Nothing scheduled for {date.toLocaleDateString()}.</p>
      ) : (
        <ul className="space-y-2">
          {events.map((e) => (
            <li key={e.id}><EventPill e={e} /></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WeekView({ anchor, eventsByDay }: { anchor: Date; eventsByDay: Map<string, Event[]> }) {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
      {days.map((d) => {
        const items = eventsByDay.get(d.toDateString()) ?? [];
        const isToday = d.toDateString() === new Date().toDateString();
        return (
          <div key={d.toISOString()} className={`rounded-xl border ${isToday?"border-amber-300/40 bg-amber-300/[0.04]":"border-white/10 bg-white/[0.02]"} p-2 min-h-[160px]`}>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">{d.toLocaleDateString(undefined,{weekday:"short"})}</p>
              <p className={`text-sm ${isToday?"text-amber-200 font-semibold":"text-white"}`}>{d.getDate()}</p>
            </div>
            <div className="space-y-1">
              {items.map((e) => <EventPill key={e.id} e={e} compact />)}
              {items.length === 0 && <p className="text-[11px] text-zinc-600">--</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthView({ anchor, eventsByDay, onPickDay }: { anchor: Date; eventsByDay: Map<string, Event[]>; onPickDay: (d: Date) => void }) {
  const gridStart = startOfWeek(startOfMonth(anchor));
  const gridEnd = endOfDay(addDays(startOfWeek(endOfMonth(anchor)), 6));
  const days: Date[] = [];
  for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) days.push(new Date(d));
  const currentMonth = anchor.getMonth();
  return (
    <div>
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((w) => <div key={w} className="px-2">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const items = eventsByDay.get(d.toDateString()) ?? [];
          const inMonth = d.getMonth() === currentMonth;
          const isToday = d.toDateString() === new Date().toDateString();
          return (
            <button
              key={d.toISOString()}
              onClick={() => onPickDay(d)}
              className={`text-left min-h-[96px] rounded-lg border p-1.5 transition ${inMonth?"bg-white/[0.02]":"bg-white/[0.01] opacity-60"} ${isToday?"border-amber-300/40":"border-white/10"} hover:border-amber-300/40`}
            >
              <p className={`text-xs ${isToday?"text-amber-200 font-semibold":"text-white"}`}>{d.getDate()}</p>
              <div className="mt-1 space-y-0.5">
                {items.slice(0,3).map((e) => {
                  const s = TYPE_STYLES[e.type];
                  return (
                    <div key={e.id} className="flex items-center gap-1 min-w-0">
                      <span className={`size-1.5 rounded-full ${s.dot} shrink-0`} />
                      <span className="text-[10px] text-zinc-300 truncate">{e.title}</span>
                    </div>
                  );
                })}
                {items.length > 3 && <p className="text-[10px] text-amber-300">+{items.length - 3} more</p>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}